import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HeaderActionsService } from '@tourfold/libraries/shared-ui/header-actions/header-actions.service';
import { ResourceAccessService } from '@tourfold/shared-kernels/resource-access';

const ROB_OPEN_KEY = 'tourfold.aiAssistant.open';

/**
 * Single owner of the Ask Rob panel's open/closed state, shared by every shell that hosts the
 * panel (app shell, settings shell, future shells). Keeping it here — instead of a per-shell
 * signal — means the panel survives navigation between shells and page reloads (TF-2134).
 *
 * The open flag is persisted to localStorage; {@link isPanelVisible} additionally gates on the
 * runtime resource-access check so a persisted "open" never mounts a panel the user can't use
 * (tenant settings flipped, grant revoked between sessions).
 */
@Injectable({ providedIn: 'root' })
export class AiAssistantPanelService {
  private readonly resourceAccessService = inject(ResourceAccessService);
  private readonly headerActionsService = inject(HeaderActionsService);

  private readonly _isOpen = signal<boolean>(readPersistedRobOpenFlag());
  /** Whether the user wants the panel open (persisted, not access-gated). */
  readonly isOpen = this._isOpen.asReadonly();

  /** Whether a shell should actually render the panel: open AND readable. */
  readonly isPanelVisible = computed(
    () => this._isOpen() && this.resourceAccessService.canPerform('aiAssistant', 'read')
  );

  constructor() {
    // The header's Ask Rob button fires this Subject; the service owns the toggle so every
    // mounted shell reflects the same state.
    this.headerActionsService.aiAssistant
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.toggle());

    effect(() => writePersistedRobOpenFlag(this._isOpen()));
  }

  toggle(): void {
    this._isOpen.update((open) => !open);
  }

  open(): void {
    this._isOpen.set(true);
  }

  close(): void {
    this._isOpen.set(false);
  }
}

function readPersistedRobOpenFlag(): boolean {
  try {
    return globalThis.localStorage?.getItem(ROB_OPEN_KEY) === 'true';
  } catch {
    return false;
  }
}

function writePersistedRobOpenFlag(open: boolean): void {
  try {
    globalThis.localStorage?.setItem(ROB_OPEN_KEY, open ? 'true' : 'false');
  } catch {
    // localStorage may be unavailable (private mode, etc.) — silently no-op.
  }
}
